import {json, type LoaderFunction} from '@remix-run/node'
import {Link, useLoaderData, useRouteLoaderData} from '@remix-run/react'
import {slugToText, textToSlug} from '@roundforest/text-transforms-commons'

import {fetchL2Categories} from '~/api/home-page-fetcher.server'
import type {L2CategoryProps, RootLoaderResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import HomeCategoriesHeader from '~/components/home-categories/home-categories-header'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'

import {fakeL2Categories} from '~/fixtures/home-page-popular-categories-l2'
import {setEnvironmentWith} from '~/utils/async-storage.server'

export {meta} from '../_index/route'

export const handle = {
  breadcrumb: 'subCategory',
}
export const loader: LoaderFunction = async ({params, context}) => {
  setEnvironmentWith(context)

  const topCategory = textToSlug(params?.topCategory || '')
  const subCategory = textToSlug(params?.subCategory || '')

  if (process.env.NODE_ENV === 'development') {
    return json(fakeL2Categories)
  }

  const response = await fetchL2Categories({
    slug1: topCategory,
    slug2: subCategory,
  })

  if (response.type === 'success' && response?.value) {
    return json<L2CategoryProps>(response?.value)
  } else {
    throw new Response('Not Found', {status: 404})
  }
}

const Categories = () => {
  const categories = useLoaderData<L2CategoryProps>()
  const {language} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)

  const sendWidgetClickEvent = useReportWidgetClick()
  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'home-page-level-3-category-list',
    widget_place: 'main',
  })

  return (
    <>
      <HomeCategoriesHeader />
      <main className="flex w-full justify-center bg-white" ref={widgetRef}>
        <div className="content-container flex flex-col divide-y-[1px] py-14">
          {categories?.l3Categories?.map(({slug, image, productsAnalyzed}, index) => (
            <Link
              reloadDocument
              data-testid={`l2-categories-list-item-${index + 1}`}
              to={`/${slug}`}
              aria-label={`go to ${slugToText(slug)}`}
              key={`${slug}-${index}`}
              className="flex flex-row items-center gap-3 py-5"
              onClick={() => {
                sendWidgetClickEvent({
                  widget_name: `home-page-level-3-grid-item-${slug}`,
                  widget_place: 'main',
                })
              }}
            >
              <div className="flex h-16 w-16 items-center rounded-lg bg-neutral-100 bg-cover p-2 mix-blend-darken">
                <img src={image} alt={slug} className="h-full w-full mix-blend-darken" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm uppercase">{slugToText(slug)}</p>
                <p className="text-xs font-light text-neutral-500">
                  {`${productsAnalyzed} ${translations.Home.productsAnalyzed}`}{' '}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </>
  )
}

export default Categories
